"use client";

import type React from "react";
import { useContentEditableField } from "./useContentEditableField";

/** Tags this field can render as — kept to the inline/block text elements builders actually use. */
export type InlineEditableTag = "h1" | "h2" | "h3" | "span" | "div" | "p";

export interface InlineEditableTextProps {
  /** HTML tag to render. */
  tag?: InlineEditableTag;
  /** Current committed value. */
  value: string;
  /** Placeholder shown (in muted styling) when the value is empty and not editing. */
  placeholder?: string;
  /** Classes applied to the element in every state. */
  className?: string;
  /** Inline styles applied in every state — use to match a token-driven look between edit and idle. */
  style?: React.CSSProperties;
  /** Whether this field is currently being edited (owned by the caller). */
  isEditing: boolean;
  /** Commit handler — receives the trimmed, length-clamped text. */
  onCommit: (value: string) => void;
  /** Cancel handler (Escape) — the element's text is restored before this fires. */
  onCancel: () => void;
  /** Enter-edit-mode handler (click / Enter / Space). */
  onRequestEdit: () => void;
  /** Optional client-side length clamp for pasted/committed text. */
  maxLength?: number;
  /** Read-only mode: renders static text with no edit affordances. */
  disabled?: boolean;
  /** Accessible label for the idle (button-role) affordance. Defaults to the placeholder. */
  ariaLabel?: string;
}

/**
 * Presentational inline text editor: renders a static, click-to-edit element that becomes
 * `contentEditable` in place while editing, preserving the exact tag/classes so the text doesn't
 * shift when entering edit mode. All DOM mechanics come from {@link useContentEditableField}; this
 * component only wires props and applies the shared idle/editing/placeholder styling.
 *
 * @param props - Tag, value, edit state, and commit/cancel/request-edit callbacks
 * @returns The rendered editable (or static, when disabled) text element
 */
export function InlineEditableText({
  tag: Tag = "div",
  value,
  placeholder,
  className,
  style,
  isEditing,
  onCommit,
  onCancel,
  onRequestEdit,
  maxLength,
  disabled,
  ariaLabel,
}: InlineEditableTextProps): React.ReactElement {
  const { ref, fieldProps } = useContentEditableField({
    isEditing,
    value,
    onCommit,
    onCancel,
    onRequestEdit,
    maxLength,
    disabled,
  });

  const isEmpty = !value;
  const displayText = value || placeholder || "";

  if (disabled) {
    return (
      <Tag className={className} style={style}>
        {displayText}
      </Tag>
    );
  }

  const stateClass = !isEditing ? " cursor-pointer transition-opacity hover:opacity-80" : " outline-none";
  const placeholderClass = isEmpty && !isEditing ? " !text-gray-400 !font-medium" : "";

  return (
    <Tag
      ref={ref as React.Ref<never>}
      className={`${className || ""}${stateClass}${placeholderClass}`}
      style={style}
      aria-label={!isEditing ? (ariaLabel ?? placeholder) : undefined}
      {...fieldProps}
    >
      {isEditing ? undefined : displayText}
    </Tag>
  );
}
